import { displayPair } from "../supernova/supernovaSlots";

/**
 * walletRegistry — wire shaping, SIWE inspection and error classification for
 * the multi-wallet registry (ARC-46). Pure functions only; no network, no React.
 *
 * The engine (ARC-41) serves the registry:
 *
 *   GET    /wallets                 → { wallets: [{ address, label, isPrimary,
 *                                        verifiedAt, chainId, automatedPairs }] }
 *   POST   /wallets                 { wallet, signature, nonce, label? }
 *   PATCH  /wallets/:address        { label }          — cosmetic, no signature
 *   DELETE /wallets/:address
 *
 * Attaching reuses the SAME proof path as enrollment — GET /enroll/nonce, the
 * wallet personal_signs the server's message verbatim, then POST. One proof
 * implementation, one place to get it right (enrollFlow.js holds the enrollment
 * half; this module holds the registry half's shaping and copy).
 *
 * A signature is FREE and off-chain. It sends no transaction, moves nothing and
 * grants no spending or trading permission — it only proves the browser holds
 * the key for that address. The member's wallet signs; this app never does.
 *
 * COMPLIANCE: every string below is account management — addresses, labels,
 * counts, and what went wrong. No return, price, projection or promise.
 */

/* ── ARC-48: removing the enrolled wallet ─────────────────────────────────
   The engine refuses to detach the primary wallet with a 409 today, so a
   Remove control on that row could only ever fail. Until ARC-48 lands it
   renders DISABLED with the reason — never absent, because a member who can
   see the row deserves to know why the action is missing. Flip this to true
   when ARC-48 is on main, and the row's Remove goes live with no other
   change. (ARC-46 acceptance criterion 10.) */
export const PRIMARY_DETACH_SUPPORTED = false;

export const PRIMARY_DETACH_REASON =
  "This is your enrolled trading wallet, so it can't be removed here yet. " +
  "Add and verify another wallet to trade from a different address.";

/** Classified registry failure kinds — one per thing a member can do about it. */
export const WALLET_ERROR = Object.freeze({
  SIGNATURE_REJECTED: "signature-rejected",
  SIGNATURE_MISMATCH: "signature-mismatch",
  NONCE_EXPIRED: "nonce-expired",
  ALREADY_ATTACHED: "already-attached",
  HAS_AUTOMATED_PAIRS: "has-automated-pairs",
  IS_PRIMARY: "is-primary",
  NOT_ENROLLED: "not-enrolled",
  UNSUPPORTED: "unsupported",
  NOT_CONFIGURED: "not-configured",
  AUTH: "auth",
  NETWORK: "network",
  OTHER: "other",
});

/* ── engine text → member text ────────────────────────────────────────────
   The engine normalizes pair names internally ("BTC/USD", "btcusd" and
   "BTC-USD" all become "BTCUSD") and its refusals quote that form back. This
   tab surfaces those refusals VERBATIM — they carry the member's actual next
   step, which a generic "removal failed" cannot — so the normalized name would
   reach the screen as "BTCUSD" unless it is mapped on the way out.

   Presentation only. The engine's normalization is correct and is not the
   thing to change (ARC-41 review, 2026-08-14). */
const PAIR_TOKEN = /\b([A-Z0-9]{2,10}(?:USDC|USDT|USD))\b/g;

/**
 * Rewrite every engine-normalized pair name inside a free-text string into its
 * display form. Leaves text that is already sliced ("BTC/USD") alone, and
 * leaves anything it cannot split untouched rather than guessing.
 */
export function displayEngineText(text) {
  if (typeof text !== "string" || !text) return "";
  return text.replace(PAIR_TOKEN, (token) => {
    const shown = displayPair(token);
    return shown.includes("/") ? shown : token;
  });
}

/* ── SIWE inspection ──────────────────────────────────────────────────────
   EIP-4361's first line is `<domain> wants you to sign in with your Ethereum
   account:`, and the body carries `URI: <uri>`. We READ them off the server's
   message — we never rebuild the message, because a client-authored SIWE
   message is how replay and phishing bugs get in.

   Why the member is shown the domain BEFORE the wallet opens: the domain is
   the only part of the request that says WHO is asking. A member who signs
   whatever appears has no defence against a page that asks on someone else's
   behalf; a member who has read the domain does. */

/** The domain the message claims, or null if the message doesn't declare one. */
export function siweDomainOf(message) {
  const line = String(message || "").split("\n")[0] || "";
  const m = /^(\S+) wants you to sign in with your Ethereum account:$/.exec(line.trim());
  return m ? m[1] : null;
}

/** The `URI:` line, or null. Shown alongside the domain, never compared. */
export function siweUriOf(message) {
  const m = /^URI: (.+)$/m.exec(String(message || ""));
  return m ? m[1].trim() : null;
}

/**
 * The domain this build expects the engine to be asking on behalf of.
 * REACT_APP_SIWE_DOMAIN where the deployment sets one (it must match the
 * engine's SIWE_DOMAIN), otherwise the host actually serving this page — which
 * is the honest default: if the page you are on is the page asking, they agree.
 */
export function expectedSiweDomain() {
  const configured = process.env.REACT_APP_SIWE_DOMAIN;
  if (typeof configured === "string" && configured.trim()) return configured.trim();
  if (typeof window !== "undefined" && window.location?.host) return window.location.host;
  return null;
}

/**
 * What to show the member before the wallet opens.
 *
 * `matches` is only ever true when we have BOTH sides and they agree. An
 * undeclared domain, or no expectation to check it against, is `null` — an
 * unknown, which the panel states as one rather than rendering as a tick.
 *
 * @returns {{domain: ?string, uri: ?string, expected: ?string, matches: ?boolean}}
 */
export function inspectSiwe(message) {
  const domain = siweDomainOf(message);
  const expected = expectedSiweDomain();
  return {
    domain,
    uri: siweUriOf(message),
    expected,
    matches: domain && expected ? domain === expected : null,
  };
}

/* ── joining the registry to the slot pool ────────────────────────────────── */

const addrKey = (a) => String(a || "").toLowerCase();

/**
 * Attach each wallet's staked count from `/status.staking.perWallet` (ARC-42),
 * matched on address.
 *
 * `staked: null` is deliberate and means "the engine did not count for this
 * wallet" — a wallet the pool does not mention, or one it declined to count.
 * It renders as an em dash, never a zero: the same rule slotGate.js holds for
 * the pool as a whole. A wrong zero next to an address reads as "your stake
 * isn't there", which is a different and much more alarming claim than "we
 * don't know".
 */
export function withStakeCounts(wallets, perWallet) {
  const byAddress = new Map();
  (Array.isArray(perWallet) ? perWallet : []).forEach((w) => {
    if (w && w.address) byAddress.set(addrKey(w.address), w);
  });
  return (Array.isArray(wallets) ? wallets : []).map((w) => {
    const hit = byAddress.get(addrKey(w.address));
    const staked = typeof hit?.staked === "number" && hit.staked >= 0 ? hit.staked : null;
    return { ...w, staked };
  });
}

/** Is this address already on the account? Case-insensitive, as addresses are. */
export function hasWallet(wallets, address) {
  return (Array.isArray(wallets) ? wallets : []).some(
    (w) => addrKey(w.address) === addrKey(address)
  );
}

/**
 * Why this wallet's Remove is unavailable, or null when it is available.
 * Checked before the request so the member is not sent to the engine for a
 * refusal we already know is coming.
 */
export function removeBlockReason(wallet) {
  if (!wallet) return null;
  if (wallet.isPrimary && !PRIMARY_DETACH_SUPPORTED) return PRIMARY_DETACH_REASON;
  return null;
}

/* ── error classification ─────────────────────────────────────────────────── */

// EIP-1193 user-rejected-request, plus the message wallets actually show.
const isUserRejection = (err) =>
  err?.code === 4001 ||
  err?.name === "UserRejectedRequestError" ||
  /user rejected|user denied|rejected the request/i.test(err?.message || "");

const statusOf = (err) => {
  if (Number.isFinite(err?.status)) return err.status;
  const m = /^HTTP_(\d{3})$/.exec(err?.code || "");
  return m ? Number(m[1]) : null;
};

/** The engine's own sentence, when it sent one. This is what makes a refusal
 *  actionable — "deallocate BTC/USD first" instead of "removal failed". */
const engineText = (err) =>
  typeof err?.body?.error === "string" && err.body.error.trim()
    ? displayEngineText(err.body.error.trim())
    : null;

const result = (kind, message, extra = {}) => ({
  kind,
  message,
  retryable: true,
  showSupport: false,
  ...extra,
});

/**
 * Map any failure from the registry onto a member-facing state.
 *
 * Each distinct thing that can go wrong gets its OWN sentence naming the cause
 * and the next step, because "something went wrong" is the same message for a
 * cancelled signature, an expired nonce and a wallet that belongs to somebody
 * else — three situations with three different actions.
 *
 * @param {Error} err
 * @param {{action?: "add"|"remove"|"label"|"list"}} [opts]
 * @returns {{kind: string, message: string, retryable: boolean, showSupport: boolean}}
 */
export function classifyWalletError(err, { action = "add" } = {}) {
  if (!err) return null;

  if (isUserRejection(err)) {
    return result(
      WALLET_ERROR.SIGNATURE_REJECTED,
      "You declined the signature request in your wallet, so no wallet was added. " +
        "The signature only proves you control the address — it's free, it isn't a " +
        "transaction, and it grants no permission to move your funds."
    );
  }

  if (err.code === "UNSUPPORTED_CLIENT") {
    return result(
      WALLET_ERROR.UNSUPPORTED,
      "This build can't reach the wallet registry, so nothing was sent. The engine " +
        "client needs the /wallets endpoints before wallets can be added or removed.",
      { retryable: false }
    );
  }

  if (err.code === "NOT_CONFIGURED") {
    return result(
      WALLET_ERROR.NOT_CONFIGURED,
      "The Strategy Automation backend isn't connected in this environment, so wallets " +
        "can't be managed here.",
      { retryable: false }
    );
  }

  if (err.code === "NETWORK") {
    return result(
      WALLET_ERROR.NETWORK,
      "Couldn't reach the Strategy Automation backend. Check your connection and try " +
        "again — nothing was changed."
    );
  }

  const status = statusOf(err);
  const text = engineText(err);

  /* 401 covers two different events on POST /wallets, and the engine's own
     wording is what tells them apart: a proof that did not verify, and a nonce
     that was never outstanding (or has already been spent — they are
     single-use). One says "sign again with the right wallet", the other says
     "start again for a fresh request", so they must not share a sentence. */
  if (/no outstanding nonce|nonces are single-use|expired|stale|no longer valid/i.test(
    text || err.message || ""
  )) {
    return result(
      WALLET_ERROR.NONCE_EXPIRED,
      "That sign-in request expired before it was signed — they're single-use and " +
        "short-lived. Start Add wallet again to get a fresh one. Nothing was added."
    );
  }

  if (/wallet proof failed|does not match|nonce was issued to a different account/i.test(
    text || err.message || ""
  )) {
    return result(
      WALLET_ERROR.SIGNATURE_MISMATCH,
      "That signature didn't come from the wallet you're adding, so it wasn't accepted " +
        "and nothing was added. Switch your wallet extension to the address you want to " +
        "add, then run Add wallet again."
    );
  }

  if (status === 409) {
    /* Two different 409s, and the engine names which. Detaching a wallet with
       strategies on it is a to-do list; a wallet on someone else's account is
       not something the member can fix by trying again. */
    if (/automated pair/i.test(text || "")) {
      return result(WALLET_ERROR.HAS_AUTOMATED_PAIRS, text, { retryable: false });
    }
    if (/enrolled trading wallet|cannot be removed/i.test(text || "")) {
      return result(WALLET_ERROR.IS_PRIMARY, text, { retryable: false });
    }
    if (action === "remove" && text) {
      return result(WALLET_ERROR.OTHER, text, { retryable: false });
    }
    return result(
      WALLET_ERROR.ALREADY_ATTACHED,
      "This wallet is already attached to a different ARCrypto account. One wallet " +
        "belongs to one account, and for security we can't move it automatically. If " +
        "it's yours, contact support and we'll sort it out.",
      { retryable: false, showSupport: true }
    );
  }

  if (status === 404 || err.code === "HTTP_404") {
    return result(
      WALLET_ERROR.NOT_ENROLLED,
      "Enroll your first wallet before adding another — enrollment is what creates your " +
        "account with the engine. Use “Your signal setup” below.",
      { retryable: false }
    );
  }

  if (err.code === "AUTH" || err.code === "NO_AUTH" || status === 401 || status === 403) {
    return result(
      WALLET_ERROR.AUTH,
      "Your session isn't authorized for Strategy Automation. Try signing out and back " +
        "in, then start again."
    );
  }

  return result(
    WALLET_ERROR.OTHER,
    text ||
      err.message ||
      "That didn't complete. Nothing was changed — please try again."
  );
}

export default classifyWalletError;
